import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React from 'react';
import {
  AppWindow,
  BookOpen,
  BookOpenText,
  Building2,
  ChartBarIcon,
  CircleUser,
  ClipboardList,
  FileText,
  Folder,
  FolderKanban,
  GalleryVerticalEnd,
  Network,
  Info,
  LayoutGrid,
  RefreshCw,
  Server,
  ShieldCheck,
  Sparkles,
  Database,
  Users,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { domainService } from '../services/domainService';
import { useCaseService } from '../services/useCaseService';
import cadrageService from '../services/cadrageService';
import forgeService from '../services/forgeService';
import organizationService from '../services/organizationService';
import projectService from '../services/projectService';
import { chatbotsService } from '../services/chatbots';
import { conversationsService } from '../services/conversations';

export interface BreadcrumbItem {
  icon?: React.ElementType;
  label: string;
  href?: string;
}

export interface UseBreadcrumbsResult {
  items: BreadcrumbItem[];
  orgName: string;
  /** The current org is the empty GenieFactory management org (super admins only). */
  isPlatformOrg: boolean;
}

const truncate = (s: string, max = 25) =>
  s.length > max ? s.slice(0, max) + '...' : s;

const MAX_BREADCRUMB_LEVELS = 3;

export function useBreadcrumbs(): UseBreadcrumbsResult {
  const location = useLocation();
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const [orgName, setOrgName] = useState('');
  const [isPlatformOrg, setIsPlatformOrg] = useState(false);
  const [segments, setSegments] = useState<BreadcrumbItem[]>([]);

  // Fetch org name once (re-fetch on org change)
  useEffect(() => {
    if (user?.organization_id) {
      organizationService.getMyOrganization()
        .then(org => {
          setOrgName(truncate(org.name));
          setIsPlatformOrg(org.is_platform === true);
        })
        .catch(() => {
          setOrgName('');
          setIsPlatformOrg(false);
        });
    } else {
      setOrgName('');
      setIsPlatformOrg(false);
    }
  }, [user?.organization_id]);

  // Build breadcrumbs on every pathname or orgName change
  useEffect(() => {
    let cancelled = false;

    const build = async () => {
      const pathname = location.pathname;
      const parts = pathname.split('/').filter(Boolean);
      const searchParams = new URLSearchParams(location.search);

      const dashRoot: BreadcrumbItem = {
        icon: LayoutGrid,
        label: orgName || t('layout.dashboard'),
        href: '/welcome',
      };

      const adminRoot: BreadcrumbItem = {
        icon: ShieldCheck,
        label: t('sidebar.administration'),
      };

      const guideRoot: BreadcrumbItem = {
        icon: BookOpen,
        label: t('sidebar.documentation.root'),
        href: '/documentation',
      };

      let result: BreadcrumbItem[] = [dashRoot];

      try {
        // ── DASHBOARD ──────────────────────────────────────────────────────────
        if (pathname === '/dashboard') {
          result = [dashRoot];
        }

        // ── DOMAINS ────────────────────────────────────────────────────────────
        else if (pathname === '/domains') {
          result = [dashRoot, { icon: LayoutGrid, label: t('sidebar.domains.root'), href: '/domains' }];
        }
        else if (pathname === '/domains/new') {
          result = [dashRoot, { icon: LayoutGrid, label: t('sidebar.domains.root'), href: '/domains' }, { icon: LayoutGrid, label: t('layout.pages.domains.create') }];
        }
        else if (parts[0] === 'domains' && parts.length > 1) {
          const domainId = parts[1];
          let domainName = '…';
          try {
            const domain = await domainService.getDomainDetails(domainId);
            domainName = domain.name;
          } catch { /* keep placeholder */ }

          if (parts.includes('edit')) {
            result = [dashRoot, { icon: LayoutGrid, label: domainName, href: `/domains/${domainId}` }, { icon: LayoutGrid, label: t('layout.pages.domains.edit') }];
          } else {
            result = [dashRoot, { icon: LayoutGrid, label: domainName }];
          }
        }

        // ── USE CASES ──────────────────────────────────────────────────────────
        else if (pathname === '/cas-usage') {
          result = [dashRoot, { icon: ClipboardList, label: t('breadcrumb.useCasesDashboard'), href: '/cas-usage' }];
        }
        else if (pathname === '/cas-usage/list') {
          result = [dashRoot, { icon: ClipboardList, label: t('sidebar.useCases.root'), href: '/cas-usage/list' }];
        }
        else if (pathname === '/cas-usage/new') {
          result = [dashRoot, { icon: ClipboardList, label: t('sidebar.useCases.root'), href: '/cas-usage/list' }, { icon: ClipboardList, label: t('layout.pages.useCases.create') }];
        }
        else if (parts[0] === 'cas-usage' && parts.length > 1) {
          const ucId = parts[1];
          let ucName = '…';
          let domainName = '';
          let domainHref = '';
          try {
            const uc = await useCaseService.get(ucId);
            ucName = uc.name;
            if (uc.domain_id) {
              const domain = await domainService.getDomainDetails(uc.domain_id);
              domainName = domain.name;
              domainHref = `/domains/${uc.domain_id}`;
            }
          } catch { /* keep placeholders */ }

          result = [
            dashRoot,
            ...(domainName ? [{ icon: LayoutGrid, label: domainName, href: domainHref } as BreadcrumbItem] : []),
            { icon: ClipboardList, label: ucName },
          ];
        }

        // ── PROJECTS ───────────────────────────────────────────────────────────
        else if (pathname === '/projets') {
          result = [dashRoot, { icon: FolderKanban, label: t('sidebar.projects'), href: '/projets' }];
        }
        else if (parts[0] === 'projets' && parts.length > 1) {
          let projectTitle = '…';
          try {
            const project = await projectService.get(parts[1]);
            projectTitle = truncate(project.title);
          } catch { /* keep placeholder */ }
          result = [
            dashRoot,
            { icon: FolderKanban, label: t('sidebar.projects'), href: '/projets' },
            { icon: FolderKanban, label: projectTitle },
          ];
        }
        else if (pathname === '/galerie') {
          result = [dashRoot, { icon: GalleryVerticalEnd, label: t('sidebar.gallery'), href: '/galerie' }];
        }
        else if (pathname === '/portefeuille') {
          result = [dashRoot, { icon: Network, label: t('sidebar.portfolio'), href: '/portefeuille' }];
        }

        // ── CADRAGES ───────────────────────────────────────────────────────────
        else if (pathname === '/cadrages') {
          result = [dashRoot, { icon: FileText, label: t('sidebar.cadrages.root'), href: '/cadrages' }];
        }
        else if (pathname === '/cadrages/new') {
          result = [dashRoot, { icon: FileText, label: t('sidebar.cadrages.root'), href: '/cadrages' }, { icon: FileText, label: t('layout.pages.cadrages.create') }];
        }
        else if (parts[0] === 'cadrages' && parts.length > 1) {
          const cadrageId = parts[1];
          let cadrageName = '…';
          let ucName = '';
          let ucHref = '';
          let domainName = '';
          let domainHref = '';
          try {
            const cadrage = await cadrageService.getCadrageById(cadrageId);
            cadrageName = cadrage.name;
            if (cadrage.use_case_id) {
              const uc = await useCaseService.get(cadrage.use_case_id);
              ucName = uc.name;
              ucHref = `/cas-usage/${cadrage.use_case_id}`;
              if (uc.domain_id) {
                const domain = await domainService.getDomainDetails(uc.domain_id);
                domainName = domain.name;
                domainHref = `/domains/${uc.domain_id}`;
              }
            }
          } catch { /* keep placeholders */ }

          result = [
            dashRoot,
            ...(domainName ? [{ icon: LayoutGrid, label: domainName, href: domainHref } as BreadcrumbItem] : []),
            ...(ucName ? [{ icon: ClipboardList, label: ucName, href: ucHref } as BreadcrumbItem] : []),
            { icon: FileText, label: cadrageName },
          ];
        }

        // ── ADMIN: ORGANIZATIONS ───────────────────────────────────────────────
        else if (pathname === '/organizations') {
          result = [adminRoot, { icon: Building2, label: t('sidebar.organizations.root'), href: '/organizations' }];
        }
        else if (parts[0] === 'organizations' && parts.length > 1) {
          const orgId = parts[1];
          let orgDetailName = '…';
          try {
            const org = await organizationService.getOrganizationById(orgId);
            orgDetailName = org.name;
          } catch { /* keep placeholder */ }
          result = [adminRoot, { icon: Building2, label: t('sidebar.organizations.root'), href: '/organizations' }, { icon: Building2, label: orgDetailName }];
        }

        // ── ADMIN: USERS ───────────────────────────────────────────────────────
        else if (pathname === '/users') {
          result = [adminRoot, { icon: Users, label: t('sidebar.users'), href: '/users' }];
        }

        // ── ADMIN: PROFILES ─────────────────────────────────────────────────────
        // Profile page (/admin/profiles/new, /admin/profiles/:id/:tab) keeps the list as parent.
        else if (pathname === '/admin/profiles' || pathname.startsWith('/admin/profiles/')) {
          result = [adminRoot, { icon: ShieldCheck, label: t('sidebar.profiles'), href: '/admin/profiles' }];
        }

        // ── ADMIN: LOGIN AS ─────────────────────────────────────────────────────
        else if (pathname === '/admin/login-as') {
          result = [adminRoot, { icon: CircleUser, label: t('sidebar.loginAs'), href: '/admin/login-as' }];
        }

        // ── ADMIN: FORGE DASHBOARD ─────────────────────────────────────────────
        else if (pathname === '/forge-dashboard') {
          result = [adminRoot, { icon: Database, label: t('sidebar.forgeDashboard'), href: '/forge-dashboard' }];
        }

        // ── MY ORGANIZATION ────────────────────────────────────────────────────
        else if (pathname === '/my-organization') {
          result = [dashRoot, { icon: Building2, label: t('layout.pages.organizations.mine') }];
        }

        // ── FORGES ─────────────────────────────────────────────────────────────
        else if (pathname === '/forges') {
          result = [dashRoot, { icon: Server, label: t('sidebar.sections.deployment'), href: '/forges' }, { icon: Server, label: t('sidebar.forge.forges'), href: '/forges' }];
        }
        else if (parts[0] === 'forges' && parts.length > 1) {
          const forgeId = parts[1];
          let forgeName = '…';
          try {
            const forge = await forgeService.getForge(forgeId);
            forgeName = forge.display_name || forge.name;
          } catch { /* keep placeholder */ }
          result = [
            dashRoot,
            { icon: Server, label: t('sidebar.sections.deployment'), href: '/forges' },
            { icon: Server, label: t('sidebar.forge.forges'), href: '/forges' },
            { icon: Server, label: forgeName },
          ];
        }

        // ── APPLICATIONS ───────────────────────────────────────────────────────
        else if (pathname === '/applications') {
          result = [dashRoot, { icon: Server, label: t('sidebar.sections.deployment'), href: '/forges' }, { icon: AppWindow, label: t('sidebar.forge.applications'), href: '/applications' }];
        }
        else if (parts[0] === 'applications' && parts.length > 1) {
          const appId = parts[1];
          let appName = '…';
          try {
            const app = await forgeService.getApplication(appId);
            appName = app.name;
          } catch { /* keep placeholder */ }
          result = [
            dashRoot,
            { icon: Server, label: t('sidebar.sections.deployment'), href: '/forges' },
            { icon: AppWindow, label: t('sidebar.forge.applications'), href: '/applications' },
            { icon: AppWindow, label: appName },
          ];
        }

        // ── PROFILE ────────────────────────────────────────────────────────────
        else if (pathname === '/conversations') {
          result = [
            dashRoot,
            { icon: Folder, label: t('sidebar.knowledge.conversations'), href: '/conversations' },
          ];
        }
        else if (pathname === '/chatbots') {
          result = [
            dashRoot,
            { icon: Folder, label: t('sidebar.knowledge.chatbots'), href: '/chatbots' },
          ];
        }
        else if (pathname === '/evaluations') {
          result = [
            dashRoot,
            { icon: ChartBarIcon, label: t('sidebar.evaluations'), href: '/evaluations' },
          ];
        }
        else if (pathname === '/knowledge-base/graph') {
          result = [
            dashRoot,
            { icon: Folder, label: t('sidebar.knowledge.knowledgeBase'), href: '/knowledge-base' },
            { icon: Network, label: t('knowledgeGraph.title') },
          ];
        }
        else if (pathname === '/admin/configuration/ontology/owl') {
          result = [
            dashRoot,
            { icon: BookOpenText, label: t('sidebar.knowledge.knowledgeManagement'), href: '/admin/configuration' },
            { icon: Network, label: t('owlEditor.title') },
          ];
        }
        else if (pathname === '/admin/configuration') {
          result = [
            dashRoot,
            { icon: BookOpenText, label: t('sidebar.knowledge.knowledgeManagement'), href: '/admin/configuration' },
          ];
        }
        else if (pathname === '/chat') {
          const conversationId = searchParams.get('conversation_id');
          const chatbotId = searchParams.get('chatbot_id');
          let chatbotName = t('sidebar.knowledge.chatbots');

          try {
            let resolvedChatbotId: string | null = chatbotId;
            if (!resolvedChatbotId && conversationId) {
              const conversation = await conversationsService.get(conversationId);
              resolvedChatbotId = conversation.chatbot_id;
            }

            if (resolvedChatbotId) {
              const chatbot = await chatbotsService.get(resolvedChatbotId);
              chatbotName = truncate(chatbot.name);
            }
          } catch {
            // Keep fallback chatbot label when lookup fails.
          }

          result = [
            dashRoot,
            { icon: Folder, label: chatbotName },
            { icon: Folder, label: 'chat' },
          ];
        }
        // ── PROFILE ────────────────────────────────────────────────────────────
        else if (pathname === '/profil' || pathname === '/profile') {
          const username = truncate(user?.name || user?.email || t('global.user'));
          result = [
            { icon: LayoutGrid, label: username, href: '/welcome' },
            { icon: CircleUser, label: t('layout.pages.profile') },
          ];
        }

        // ── DOCUMENTATION ──────────────────────────────────────────────────────
        else if (pathname === '/documentation') {
          result = [guideRoot];
        }
        else if (pathname.startsWith('/documentation/')) {
          let subIcon: React.ElementType = BookOpen;
          let subLabel = '';
          if (pathname.includes('/introduction')) {
            subIcon = Info;
            subLabel = t('sidebar.documentation.introduction');
          } else if (pathname.includes('/workflow')) {
            subIcon = RefreshCw;
            subLabel = t('sidebar.documentation.workflow');
          } else if (pathname.includes('/audits')) {
            subIcon = ClipboardList;
            subLabel = t('sidebar.documentation.audits');
          } else if (pathname.includes('/use-cases')) {
            subIcon = ClipboardList;
            subLabel = t('sidebar.documentation.useCases');
          } else if (pathname.includes('/cadrages')) {
            subIcon = FileText;
            subLabel = t('sidebar.documentation.cadrages');
          } else if (pathname.includes('/ai-features')) {
            subIcon = Sparkles;
            subLabel = t('sidebar.documentation.aiFeatures');
          }
          result = subLabel ? [guideRoot, { icon: subIcon, label: subLabel }] : [guideRoot];
        }
      } catch (err) {
        console.error('useBreadcrumbs build error:', err);
      }

      // The organization is shown as a subtitle under the logo and is not
      // actionable, so drop the dashRoot (org-name carrier) from inline
      // breadcrumbs to free space and avoid redundancy.
      const stripped = result.filter((item) => item !== dashRoot);
      // Cap the visible chain to the deepest MAX_BREADCRUMB_LEVELS so deep
      // structures (e.g. domain → use case → solution → tab) don't push the
      // header into a multi-line wrap.
      const capped = stripped.length > MAX_BREADCRUMB_LEVELS
        ? stripped.slice(-MAX_BREADCRUMB_LEVELS)
        : stripped;
      if (!cancelled) setSegments(capped);
    };

    build();
    return () => { cancelled = true; };
  }, [location.pathname, location.search, orgName, user, t]);

  return { items: segments, orgName, isPlatformOrg };
}
